import React, { useContext, useState } from 'react';

import { Collapse, IconButton, InputLabel, MenuItem } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { Field } from 'formik';
import { CheckboxWithLabel, TextField } from 'formik-material-ui';
import { useIntl } from 'react-intl';

import { DIRECTIONS } from 'app/i18n-locale/locales-constants';
import { useStyles } from 'app/operations/drivers/driver-form';
import { LanguageContext } from 'context/intl.context';

interface FieldLabelProps {
  id: string;
  required?: boolean;
  // "?" button rendered next to the label (see FieldWrapper)
  hintButton?: React.ReactNode;
}

const FieldLabel: React.FC<FieldLabelProps> = ({
  id,
  required = true,
  hintButton,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { direction } = useContext(LanguageContext);
  const isRTL = direction === DIRECTIONS.RTL;

  return (
    <InputLabel
      className={classes.formLabel}
      style={{
        flexDirection: isRTL ? 'row' : 'row-reverse',
        alignItems: 'center',
      }}
    >
      {hintButton}

      {required && <strong className={classes.requiredAstrisk}>*</strong>}

      {intl.formatMessage({ id })}
    </InputLabel>
  );
};

interface FieldWrapperProps {
  labelId: string;
  // when set, a "?" button next to the label shows / hides this hint under the input
  hintId?: string;
  required?: boolean;
}

const FieldWrapper: React.FC<FieldWrapperProps> = ({
  labelId,
  hintId,
  required = true,
  children,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [isHintOpen, setHintOpen] = useState(false);

  return (
    <div className={classes.fieldWrapper}>
      <FieldLabel
        id={labelId}
        required={required}
        hintButton={
          hintId && (
            <IconButton
              type="button"
              size="small"
              color={isHintOpen ? 'primary' : 'default'}
              aria-label={intl.formatMessage({ id: 'orgShowHint' })}
              aria-expanded={isHintOpen}
              style={{ padding: 2, margin: '0 6px' }}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                setHintOpen(open => !open);
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          )
        }
      />

      {children}

      {hintId && (
        <Collapse in={isHintOpen}>
          <div
            style={{
              marginTop: 6,
              padding: '8px 12px',
              borderRadius: 4,
              background: '#F4F6F8',
              color: '#444',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {intl.formatMessage({ id: hintId })}
          </div>
        </Collapse>
      )}
    </div>
  );
};

// Must be rendered inside a <Formik> whose values contain
// OrganizationExtraDetailsState (the edit form, or the add-flow modal).
const OrganizationExtraFields: React.FC<{ disabled?: boolean }> = ({
  disabled = false,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <FieldWrapper labelId="orgKind" hintId="orgKindHint">
        <Field
          name="organizationKind"
          component={TextField}
          select
          variant="outlined"
          size="small"
          disabled={disabled}
        >
          <MenuItem value="my_company">
            {intl.formatMessage({ id: 'orgKindMyCompany' })}
          </MenuItem>

          <MenuItem value="is_pto">
            {intl.formatMessage({ id: 'orgKindPto' })}
          </MenuItem>
        </Field>
      </FieldWrapper>

      <FieldWrapper labelId="orgMaxDevices">
        <Field
          name="maxDevicesCanAdded"
          component={TextField}
          type="number"
          variant="outlined"
          size="small"
          disabled={disabled}
          inputProps={{ min: 0, step: 1 }}
        />
      </FieldWrapper>

      <FieldWrapper labelId="orgChargingBackendUrl">
        <Field
          name="chargingBackendBaseUrl"
          component={TextField}
          type="text"
          variant="outlined"
          size="small"
          disabled={disabled}
        />
      </FieldWrapper>

      <FieldWrapper labelId="orgThresholdValue" hintId="orgThresholdHint">
        <Field
          name="thresholdValue"
          component={TextField}
          type="number"
          variant="outlined"
          size="small"
          disabled={disabled}
          inputProps={{ step: 'any' }}
        />
      </FieldWrapper>

      <FieldWrapper
        labelId="orgServiceDayCutoff"
        hintId="orgServiceDayCutoffHint"
      >
        <Field
          name="serviceDayCutoffUtc"
          component={TextField}
          type="time"
          variant="outlined"
          size="small"
          disabled={disabled}
          inputProps={{ step: 1 }}
        />
      </FieldWrapper>

      <FieldWrapper labelId="orgCurrency">
        <Field
          name="currency"
          component={TextField}
          type="text"
          variant="outlined"
          size="small"
          disabled={disabled}
          inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
        />
      </FieldWrapper>

      {/* last input of the group: the Super Organization checkbox */}
      <div className={classes.fieldWrapper}>
        <Field
          name="isSuperOrganisation"
          type="checkbox"
          component={CheckboxWithLabel}
          disabled={disabled}
          Label={{ label: intl.formatMessage({ id: 'orgIsSuper' }) }}
        />
      </div>
    </div>
  );
};

export { OrganizationExtraFields };
